import type { InspectionStatus, Severity, RiskLevel, FindingStatus, CAPAStatus, CAPAPriority, ColorCode, JobClassification } from '../types';

// --- Date Formatting ---
export function formatDate(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function timeAgo(dateStr: string): string {
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 7) return `${diffDay}d ago`;
  return formatDate(dateStr);
}

// --- Number Formatting ---
export function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// --- Status Colors & Labels ---
export function getInspectionStatusLabel(status: InspectionStatus): string {
  const labels: Record<InspectionStatus, string> = {
    draft: 'Draft', in_progress: 'In Progress', completed: 'Completed',
    under_review: 'Under Review', approved: 'Approved', rejected: 'Rejected',
  };
  return labels[status];
}

export function getInspectionStatusColor(status: InspectionStatus): string {
  const colors: Record<InspectionStatus, string> = {
    draft: 'bg-slate-100 text-slate-600',
    in_progress: 'bg-blue-100 text-blue-700',
    completed: 'bg-emerald-100 text-emerald-700',
    under_review: 'bg-amber-100 text-amber-700',
    approved: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-700',
  };
  return colors[status];
}

export function getSeverityColor(severity: Severity): string {
  const colors: Record<Severity, string> = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    major: 'bg-orange-100 text-orange-700 border-orange-200',
    minor: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    observation: 'bg-slate-100 text-slate-600 border-slate-200',
  };
  return colors[severity];
}

export function getRiskLevelColor(level: RiskLevel): string {
  const colors: Record<RiskLevel, string> = {
    high: 'bg-red-500',
    medium: 'bg-amber-500',
    low: 'bg-emerald-500',
  };
  return colors[level];
}

export function getRiskLevelTextColor(level: RiskLevel): string {
  const colors: Record<RiskLevel, string> = {
    high: 'text-red-600',
    medium: 'text-amber-600',
    low: 'text-emerald-600',
  };
  return colors[level];
}

export function getCAPAStatusLabel(status: CAPAStatus): string {
  const labels: Record<CAPAStatus, string> = {
    open: 'Open', assigned: 'Assigned', in_progress: 'In Progress',
    pending_review: 'Pending Review', closed: 'Closed', verified: 'Verified',
  };
  return labels[status];
}

export function getCAPAStatusColor(status: CAPAStatus): string {
  const colors: Record<CAPAStatus, string> = {
    open: 'bg-slate-100 text-slate-700',
    assigned: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-indigo-100 text-indigo-700',
    pending_review: 'bg-amber-100 text-amber-700',
    closed: 'bg-emerald-100 text-emerald-700',
    verified: 'bg-green-100 text-green-700',
  };
  return colors[status];
}

export function getCAPAPriorityColor(priority: CAPAPriority): string {
  const colors: Record<CAPAPriority, string> = {
    critical: 'bg-red-500 text-white',
    high: 'bg-orange-500 text-white',
    medium: 'bg-amber-400 text-amber-900',
    low: 'bg-slate-400 text-white',
  };
  return colors[priority];
}

export function getCAPAStatusStep(status: CAPAStatus): number {
  const steps: Record<CAPAStatus, number> = {
    open: 0, assigned: 1, in_progress: 2, pending_review: 3, closed: 4, verified: 5,
  };
  return steps[status];
}

// --- Score Color ---
export function getScoreColor(score: number): string {
  if (score >= 7.5) return 'text-emerald-600';
  if (score >= 6.0) return 'text-amber-600';
  if (score >= 4.0) return 'text-orange-600';
  return 'text-red-600';
}

export function getScoreBgColor(score: number): string {
  if (score >= 7.5) return 'bg-emerald-500';
  if (score >= 6.0) return 'bg-amber-500';
  if (score >= 4.0) return 'bg-orange-500';
  return 'bg-red-500';
}

// --- Helpers ---
export function getInitials(name: string): string {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

export function classNames(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

// --- Color Code ---
export function getColorCodeLabel(code: ColorCode): string {
  const labels: Record<ColorCode, string> = {
    red: 'RED', yellow: 'YELLOW', green: 'GREEN',
    grey_blue: 'GREY BLUE', orange: 'ORANGE', none: 'NONE',
  };
  return labels[code];
}

export function getColorCodeBg(code: ColorCode): string {
  const colors: Record<ColorCode, string> = {
    red: 'bg-red-500', yellow: 'bg-amber-400', green: 'bg-emerald-500',
    grey_blue: 'bg-slate-400', orange: 'bg-orange-500', none: 'bg-slate-200',
  };
  return colors[code];
}

export function getColorCodeHex(code: ColorCode): string {
  const colors: Record<ColorCode, string> = {
    red: '#EF4444', yellow: '#FBBF24', green: '#10B981',
    grey_blue: '#94A3B8', orange: '#F97316', none: '#E2E8F0',
  };
  return colors[code];
}

export function getColorCodeTextClass(code: ColorCode): string {
  const colors: Record<ColorCode, string> = {
    red: 'text-red-700', yellow: 'text-amber-700', green: 'text-emerald-700',
    grey_blue: 'text-slate-600', orange: 'text-orange-700', none: 'text-slate-400',
  };
  return colors[code];
}

// --- Job Classification ---
export function getJobClassificationLabel(code: JobClassification): string {
  const labels: Record<JobClassification, string> = {
    fire_safety: 'Fire Safety', machinery: 'Machinery', navigation: 'Navigation',
    environmental: 'Environmental', documentation: 'Documentation', structural: 'Structural',
    life_saving: 'Life Saving', electrical: 'Electrical', housekeeping: 'Housekeeping', other: 'Other',
  };
  return labels[code];
}

export function getJobClassificationIcon(code: JobClassification): string {
  const icons: Record<JobClassification, string> = {
    fire_safety: '🔥', machinery: '⚙️', navigation: '🧭',
    environmental: '🌿', documentation: '📋', structural: '🏗️',
    life_saving: '🛟', electrical: '⚡', housekeeping: '🧹', other: '📌',
  };
  return icons[code];
}

export function getJobClassificationColor(code: JobClassification): string {
  const colors: Record<JobClassification, string> = {
    fire_safety: 'bg-red-50 text-red-700 border-red-200',
    machinery: 'bg-blue-50 text-blue-700 border-blue-200',
    navigation: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    environmental: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    documentation: 'bg-amber-50 text-amber-700 border-amber-200',
    structural: 'bg-orange-50 text-orange-700 border-orange-200',
    life_saving: 'bg-cyan-50 text-cyan-700 border-cyan-200',
    electrical: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    housekeeping: 'bg-slate-50 text-slate-700 border-slate-200',
    other: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return colors[code];
}

export const ALL_COLOR_CODES: ColorCode[] = ['red', 'yellow', 'green', 'grey_blue', 'orange'];
export const ALL_JOB_CLASSIFICATIONS: JobClassification[] = [
  'fire_safety', 'machinery', 'navigation', 'environmental',
  'documentation', 'structural', 'life_saving', 'electrical', 'housekeeping', 'other',
];
